import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import SEO from '../components/SEO';

const News = () => {
  const { t } = useTranslation();
  const { token, isAdmin, isAuthenticated, API } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [selectedPost, setSelectedPost] = useState(null);
  const [formData, setFormData] = useState({ title: '', content: '', summary: '', is_published: true, members_only: false });

  useEffect(() => { fetchPosts(); }, []);

  const fetchPosts = async () => {
    try {
      const res = await axios.get(`${API}/blog`);
      setPosts(res.data);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const headers = { Authorization: `Bearer ${token}` };
    try {
      if (editingPost) {
        await axios.put(`${API}/blog/${editingPost.id}`, formData, { headers });
      } else {
        await axios.post(`${API}/blog`, formData, { headers });
      }
      setShowForm(false);
      setEditingPost(null);
      setFormData({ title: '', content: '', summary: '', is_published: true, members_only: false });
      fetchPosts();
    } catch (err) { alert(err.response?.data?.detail || 'Failed to save post'); }
  };

  const handleDelete = async (postId) => {
    if (!window.confirm('Delete this post?')) return;
    try {
      await axios.delete(`${API}/blog/${postId}`, { headers: { Authorization: `Bearer ${token}` } });
      fetchPosts();
    } catch (err) { alert('Failed to delete'); }
  };

  const startEdit = (post) => {
    setEditingPost(post);
    setFormData({ title: post.title, content: post.content, summary: post.summary || '', is_published: post.is_published, members_only: post.members_only });
    setShowForm(true);
    setSelectedPost(null);
  };

  if (loading) return <div style={{ textAlign: 'center', padding: '3rem' }}>{t('common.loading')}</div>;

  if (selectedPost) {
    const canView = !selectedPost.members_only || isAuthenticated;
    return (
      <div data-testid="news-detail" style={{ maxWidth: '800px', margin: '0 auto' }}>
        <button className="btn btn-secondary btn-small" onClick={() => setSelectedPost(null)} data-testid="back-to-news" style={{ marginBottom: '1.5rem' }}>&larr; {t('news.backToNews')}</button>
        <h1>{selectedPost.title}</h1>
        <p style={{ color: 'var(--muted)', marginBottom: '1.5rem' }}>
          By {selectedPost.author_name} &middot; {new Date(selectedPost.created_at).toLocaleDateString()}
          {selectedPost.members_only && <span className="badge badge-member" style={{ marginLeft: '0.5rem' }}>{t('cms.membersOnly')}</span>}
        </p>
        {canView ? (
          <div style={{ lineHeight: '1.8', whiteSpace: 'pre-wrap' }} data-testid="news-content">{selectedPost.content}</div>
        ) : (
          <div className="member-overlay-inline" data-testid="news-locked">
            <p>{t('overlay.membersOnlyDesc', { content: 'article' })}</p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div data-testid="news-page">
      <SEO path="/news" title="News & Updates" description="Latest news, research updates, and announcements from the African Federation of Hypertension Organisations and Societies." />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <div>
          <h1>{t('news.title')}</h1>
          <p style={{ color: 'var(--muted)' }}>{t('news.subtitle')}</p>
        </div>
        {isAdmin && (
          <button className="btn" onClick={() => { setShowForm(!showForm); setEditingPost(null); setFormData({ title: '', content: '', summary: '', is_published: true, members_only: false }); }} data-testid="new-post-btn">
            {showForm ? t('common.cancel') : '+ ' + t('news.newPost')}
          </button>
        )}
      </div>

      {showForm && isAdmin && (
        <form onSubmit={handleSubmit} className="card" style={{ padding: '1.5rem', marginBottom: '2rem' }} data-testid="post-form">
          <h3 style={{ marginBottom: '1rem', color: 'var(--brand)' }}>{editingPost ? t('news.editPost') : t('news.newPost')}</h3>
          <div className="form-group"><label>{t('news.postTitle')} *</label><input value={formData.title} onChange={e => setFormData(p => ({...p, title: e.target.value}))} required data-testid="post-title-input" /></div>
          <div className="form-group"><label>{t('news.summary')}</label><input value={formData.summary} onChange={e => setFormData(p => ({...p, summary: e.target.value}))} data-testid="post-summary-input" /></div>
          <div className="form-group"><label>{t('news.content')} *</label><textarea value={formData.content} onChange={e => setFormData(p => ({...p, content: e.target.value}))} required rows="8" data-testid="post-content-input" /></div>
          <div style={{ display: 'flex', gap: '1.5rem', marginBottom: '1rem' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <input type="checkbox" checked={formData.members_only} onChange={e => setFormData(p => ({...p, members_only: e.target.checked}))} data-testid="post-members-only" /> {t('cms.membersOnly')}
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <input type="checkbox" checked={formData.is_published} onChange={e => setFormData(p => ({...p, is_published: e.target.checked}))} data-testid="post-published" /> {t('news.published')}
            </label>
          </div>
          <button type="submit" className="btn" data-testid="post-submit-btn">{editingPost ? t('news.updatePost') : t('news.publishPost')}</button>
        </form>
      )}

      {posts.length === 0 ? (
        <div className="card" style={{ padding: '3rem', textAlign: 'center' }}>
          <p style={{ color: 'var(--muted)' }}>{t('news.noPosts')}</p>
        </div>
      ) : (
        <div className="news-list">
          {posts.map(post => (
            <article key={post.id} className="card news-card" data-testid={`news-card-${post.id}`} style={{ padding: '1.5rem', marginBottom: '1rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ flex: 1, cursor: 'pointer' }} onClick={() => setSelectedPost(post)}>
                  <h3 style={{ marginBottom: '0.5rem' }}>{post.title}</h3>
                  <p style={{ color: 'var(--muted)', fontSize: '0.85rem', marginBottom: '0.5rem' }}>
                    By {post.author_name} &middot; {new Date(post.created_at).toLocaleDateString()}
                    {post.members_only && <span className="badge badge-member" style={{ marginLeft: '0.5rem' }}>{t('cms.membersOnly')}</span>}
                  </p>
                  <p>{post.summary || post.content.substring(0, 150) + '...'}</p>
                </div>
                {isAdmin && (
                  <div style={{ display: 'flex', gap: '0.5rem', marginLeft: '1rem' }}>
                    <button className="btn btn-small btn-secondary" onClick={() => startEdit(post)} data-testid={`edit-post-${post.id}`}>{t('common.edit')}</button>
                    <button className="btn btn-small btn-danger" onClick={() => handleDelete(post.id)} data-testid={`delete-post-${post.id}`}>{t('common.delete')}</button>
                  </div>
                )}
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
};

export default News;
